import { useState, type FormEvent } from "react";

type ValidationTarget = "address" | "custom-usd" | "raw-amount";

interface ValidationMessage {
  text: string;
  target: ValidationTarget;
}

interface LiveAnalysisFormProps {
  busy: boolean;
  initialAddress?: string;
  onAnalyze: (input: {
    address: string;
    usdAmounts?: number[];
    rawTokenAmounts?: string[];
  }) => void;
}

export function LiveAnalysisForm({ busy, initialAddress = "", onAnalyze }: LiveAnalysisFormProps) {
  const [address, setAddress] = useState(initialAddress);
  const [customUsd, setCustomUsd] = useState("");
  const [rawAmount, setRawAmount] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [message, setMessage] = useState<ValidationMessage | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const mint = address.trim();
    if (!mint) {
      setMessage({ text: "Paste a Solana mint address first.", target: "address" });
      return;
    }
    if (!looksLikeSolanaMint(mint)) {
      setMessage({
        text: "Paste the complete Solana mint address—not a ticker or token name.",
        target: "address",
      });
      return;
    }

    if (rawAmount.trim()) {
      const raw = splitValues(rawAmount);
      if (!raw.length) {
        setMessage({ text: "Enter at least one raw token amount.", target: "raw-amount" });
        return;
      }
      if (raw.length > 6) {
        setMessage({ text: "Use at most six raw token amounts in one report.", target: "raw-amount" });
        return;
      }
      if (raw.some((value) => !isPositiveDecimal(value))) {
        setMessage({
          text: "Raw token amounts must be positive ordinary decimals, such as 1250 or 0.25.",
          target: "raw-amount",
        });
        return;
      }
      setMessage(null);
      onAnalyze({ address: mint, rawTokenAmounts: raw });
      return;
    }

    const customText = splitValues(customUsd);
    const custom = customText.map(Number);
    if (
      customText.some(
        (value, index) =>
          !isPositiveDecimal(value) ||
          !Number.isFinite(custom[index]) ||
          custom[index] < 0.01 ||
          custom[index] > 1_000_000,
      )
    ) {
      setMessage({
        text: "Dollar sizes must be ordinary numbers between $0.01 and $1,000,000.",
        target: "custom-usd",
      });
      return;
    }
    const combined = Array.from(new Set([100, 1_000, 10_000, ...custom]));
    if (combined.length > 6) {
      setMessage({
        text: "The three defaults plus your extras can contain at most six distinct sizes.",
        target: "custom-usd",
      });
      return;
    }
    const usdAmounts = custom.length ? combined : undefined;
    setMessage(null);
    onAnalyze({ address: mint, usdAmounts });
  };

  return (
    <section className="live-card" id="live-analysis" aria-labelledby="live-analysis-title">
      <div className="live-card-copy">
        <p className="eyebrow">Live · Solana mainnet · read only</p>
        <h2 id="live-analysis-title">Paste the mint. We’ll peek under the rug.</h2>
        <p>
          Is it the right token? Who can change it? Who could dump it? What happens if you sell
          at each size? Rug Lens checks all four without connecting your wallet.
        </p>
      </div>
      <form onSubmit={submit} noValidate>
        <label className="address-label" htmlFor="mint-address">
          Solana mint address
        </label>
        <div className="address-row">
          <input
            id="mint-address"
            name="mint-address"
            value={address}
            onChange={(event) => {
              setAddress(event.target.value);
              if (message?.target === "address") setMessage(null);
            }}
            placeholder="Paste the full mint address"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            disabled={busy}
            aria-invalid={message?.target === "address" || undefined}
            aria-describedby={`mint-address-help${message?.target === "address" ? " live-analysis-error" : ""}`}
          />
          <button className="button button-primary" type="submit" disabled={busy}>
            {busy ? "Reading the receipts…" : "Check this coin"}
          </button>
        </div>
        <p className="input-help" id="mint-address-help">Use the mint address, not a ticker, token name, pool, or wallet address.</p>

        <button
          className="advanced-toggle"
          type="button"
          aria-expanded={advanced}
          aria-controls="advanced-test-sizes"
          onClick={() => setAdvanced((value) => !value)}
        >
          {advanced ? "Hide" : "Show"} optional test sizes
        </button>
        {advanced && (
          <div className="advanced-fields" id="advanced-test-sizes">
            <label>
              Extra dollar size(s)
              <input
                id="extra-usd-sizes"
                name="extra-usd-sizes"
                value={customUsd}
                onChange={(event) => {
                  setCustomUsd(event.target.value);
                  if (message?.target === "custom-usd") setMessage(null);
                }}
                inputMode="decimal"
                placeholder="e.g. 250, 5000"
                disabled={busy || Boolean(rawAmount.trim())}
                aria-invalid={message?.target === "custom-usd" || undefined}
                aria-describedby={`extra-usd-help${message?.target === "custom-usd" ? " live-analysis-error" : ""}`}
              />
              <span id="extra-usd-help">Added to the default $100 / $1,000 / $10,000 ladder, up to six rows.</span>
            </label>
            <div className="advanced-or" aria-hidden="true">or</div>
            <label>
              Raw token amount(s)
              <input
                id="raw-token-amounts"
                name="raw-token-amounts"
                value={rawAmount}
                onChange={(event) => {
                  setRawAmount(event.target.value);
                  if (message?.target === "raw-amount") setMessage(null);
                }}
                inputMode="decimal"
                placeholder="e.g. 125000, 500000"
                disabled={busy}
                aria-invalid={message?.target === "raw-amount" || undefined}
                aria-describedby={`raw-amount-help${message?.target === "raw-amount" ? " live-analysis-error" : ""}`}
              />
              <span id="raw-amount-help">Use this when an independent USD price is unavailable. Recovery % will be unavailable.</span>
            </label>
          </div>
        )}
        <p
          className={message ? "form-message form-error" : "sr-only"}
          id="live-analysis-error"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          {message?.text ?? ""}
        </p>
      </form>
    </section>
  );
}

function splitValues(value: string): string[] {
  return value
    .split(/[\s,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function isPositiveDecimal(value: string): boolean {
  return /^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value) && !/^0(?:\.0+)?$/.test(value);
}

function looksLikeSolanaMint(value: string): boolean {
  return value.length >= 32 && value.length <= 44 && /^[1-9A-HJ-NP-Za-km-z]+$/.test(value);
}
